import React, { useEffect, useState, Component } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SearchIcon, ShoppingCartIcon, UserIcon, MenuIcon, XIcon, SunIcon, MoonIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useProducts } from '../context/ProductContext';
export const Header: React.FC = () => {
  const {
    theme,
    toggleTheme
  } = useTheme();
  const {
    cartCount
  } = useCart();
  const {
    categories
  } = useProducts();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  // Close mobile menu when navigating
  useEffect(() => {
    setIsMenuOpen(false);
    setUserMenuOpen(false);
    setCategoryMenuOpen(false);
  }, [location.pathname]);
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/category/all?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };
  // Group categories by type for mega menu
  const categoryGroups = {
    metals: categories.filter(cat => ['gold', 'silver', 'platinum'].includes(cat.id)),
    electronics: categories.filter(cat => ['electronics', 'technical-tools'].includes(cat.id)),
    other: categories.filter(cat => !['gold', 'silver', 'platinum', 'electronics', 'technical-tools'].includes(cat.id))
  };
  return <header className={`sticky top-0 z-50 bg-white dark:bg-gray-900 transition-all duration-300 ${scrolled ? 'shadow-md py-2' : 'shadow-sm py-4'}`} data-id="element-108">
      <div className="container mx-auto px-4" data-id="element-109">
        <div className="flex items-center justify-between" data-id="element-110">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-id="element-111">
            <span className="text-2xl font-bold text-gray-900 dark:text-white transition-transform duration-300 group-hover:scale-105" data-id="element-112">
              Annelinna
              <span className="text-yellow-500 transition-colors duration-300 group-hover:text-yellow-400" data-id="element-113">
                Pandimaja
              </span>
            </span>
          </Link>
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8" data-id="element-114">
            <Link to="/" className={`text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 font-medium transition-colors duration-200 ${location.pathname === '/' ? 'text-yellow-500 dark:text-yellow-500' : ''}`} data-id="element-115">
              Avaleht
            </Link>
            <div className="relative group" onMouseEnter={() => setIsShopOpen(true)} onMouseLeave={() => setIsShopOpen(false)} data-id="element-116">
              <button className={`flex items-center text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 font-medium transition-colors duration-200 ${location.pathname.includes('/category') ? 'text-yellow-500 dark:text-yellow-500' : ''}`} data-id="element-117">
                Pood
                <ChevronDownIcon size={16} className="ml-1 transition-transform duration-200 group-hover:rotate-180" data-id="element-118" />
              </button>
              {/* Enhanced mega menu */}
              <div className={`absolute left-0 mt-2 w-[500px] bg-white dark:bg-gray-800 rounded-md shadow-lg transition-all duration-300 transform origin-top-left ${isShopOpen ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible'}`} data-id="element-119">
                <div className="grid grid-cols-3 gap-4 p-6" data-id="element-120">
                  {/* Precious Metals */}
                  <div data-id="element-121">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 border-b border-gray-200 dark:border-gray-700 pb-2" data-id="element-122">
                      Väärismetallid
                    </h3>
                    <ul className="space-y-2" data-id="element-123">
                      {categoryGroups.metals.map(category => <li key={category.id} data-id="element-124">
                          <Link to={`/category/${category.id}`} className="flex items-center text-sm text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 transition-colors duration-200 py-1" data-id="element-125">
                            <ChevronRightIcon size={14} className="mr-2 text-yellow-500" data-id="element-126" />
                            {category.name}
                          </Link>
                        </li>)}
                    </ul>
                  </div>
                  {/* Electronics */}
                  <div data-id="element-127">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 border-b border-gray-200 dark:border-gray-700 pb-2" data-id="element-128">
                      Elektroonika
                    </h3>
                    <ul className="space-y-2" data-id="element-129">
                      {categoryGroups.electronics.map(category => <li key={category.id} data-id="element-130">
                          <Link to={`/category/${category.id}`} className="flex items-center text-sm text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 transition-colors duration-200 py-1" data-id="element-131">
                            <ChevronRightIcon size={14} className="mr-2 text-yellow-500" data-id="element-132" />
                            {category.name}
                          </Link>
                        </li>)}
                    </ul>
                  </div>
                  {/* Other Categories */}
                  <div data-id="element-133">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 border-b border-gray-200 dark:border-gray-700 pb-2" data-id="element-134">
                      Rohkem Kategooriaid
                    </h3>
                    <ul className="space-y-2" data-id="element-135">
                      {categoryGroups.other.map(category => <li key={category.id} data-id="element-136">
                          <Link to={`/category/${category.id}`} className="flex items-center text-sm text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 transition-colors duration-200 py-1" data-id="element-137">
                            <ChevronRightIcon size={14} className="mr-2 text-yellow-500" data-id="element-138" />
                            {category.name}
                          </Link>
                        </li>)}
                      <li data-id="element-139">
                        <Link to="/category/all" className="flex items-center text-sm font-medium text-yellow-500 hover:text-yellow-600 transition-colors duration-200 py-1" data-id="element-140">
                          Vaata Kõiki Kategooriaid →
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Featured products or promotions could go here */}
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-b-md" data-id="element-141">
                  <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-142">
                    Eripakkumine: Tasuta saatmine tellimustele üle 100€!
                  </p>
                </div>
              </div>
            </div>
            <Link to="/about" className={`text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 font-medium transition-colors duration-200 ${location.pathname === '/about' ? 'text-yellow-500 dark:text-yellow-500' : ''}`} data-id="element-143">
              Meist
            </Link>
            <Link to="/contact" className={`text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 font-medium transition-colors duration-200 ${location.pathname === '/contact' ? 'text-yellow-500 dark:text-yellow-500' : ''}`} data-id="element-144">
              Kontakt
            </Link>
          </nav>
          {/* Search, Cart, Profile, Theme */}
          <div className="flex items-center space-x-4" data-id="element-145">
            <form onSubmit={handleSearch} className="hidden md:flex relative" data-id="element-146">
              <input type="text" placeholder="Otsi tooteid..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-3 pr-10 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-all duration-300" data-id="element-147" />
              <button type="submit" className="absolute right-0 top-0 h-full px-3 text-gray-500 dark:text-gray-400 hover:text-yellow-500 transition-colors duration-200" data-id="element-148">
                <SearchIcon size={18} data-id="element-149" />
              </button>
            </form>
            <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors duration-200" aria-label="Toggle theme" data-id="element-150">
              {theme === 'dark' ? <SunIcon size={20} className="transition-transform duration-300 hover:rotate-45" data-id="element-151" /> : <MoonIcon size={20} className="transition-transform duration-300 hover:-rotate-45" data-id="element-152" />}
            </button>
            <Link to="/cart" className="relative p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-all duration-300 hover:scale-110" data-id="element-153">
              <ShoppingCartIcon size={20} data-id="element-154" />
              {cartCount > 0 && <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse" data-id="element-155">
                  {cartCount}
                </span>}
            </Link>
            {/* User menu dropdown */}
            <div className="relative" data-id="element-156">
              <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-all duration-300 hover:scale-110 focus:outline-none" data-id="element-157">
                <UserIcon size={20} data-id="element-158" />
              </button>
              {userMenuOpen && <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 animate-fadeIn" data-id="element-159">
                  <Link to="/account" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200" onClick={() => setUserMenuOpen(false)} data-id="element-160">
                    Minu Konto
                  </Link>
                  <Link to="/account?tab=orders" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200" onClick={() => setUserMenuOpen(false)} data-id="element-161">
                    Minu Tellimused
                  </Link>
                  <Link to="/account?tab=wishlist" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200" onClick={() => setUserMenuOpen(false)} data-id="element-162">
                    Soovinimekiri
                  </Link>
                  <div className="border-t border-gray-100 dark:border-gray-700 my-1" data-id="element-163"></div>
                  <button className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200" onClick={() => setUserMenuOpen(false)} data-id="element-164">
                    Logi Välja
                  </button>
                </div>}
            </div>
            {/* Mobile menu button */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none transition-colors duration-200" data-id="element-165">
              {isMenuOpen ? <XIcon size={20} className="transition-transform duration-300 rotate-90" data-id="element-166" /> : <MenuIcon size={20} className="transition-transform duration-300 hover:scale-110" data-id="element-167" />}
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {isMenuOpen && <div className="md:hidden mt-4 pb-4 animate-fadeIn" data-id="element-168">
            <form onSubmit={handleSearch} className="mb-4" data-id="element-169">
              <div className="relative" data-id="element-170">
                <input type="text" placeholder="Otsi tooteid..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-3 pr-10 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-all duration-300" data-id="element-171" />
                <button type="submit" className="absolute right-0 top-0 h-full px-3 text-gray-500 dark:text-gray-400 hover:text-yellow-500 transition-colors duration-200" data-id="element-172">
                  <SearchIcon size={18} data-id="element-173" />
                </button>
              </div>
            </form>
            <div className="flex flex-col space-y-3" data-id="element-174">
              <Link to="/" className={`text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 font-medium transition-colors duration-200 ${location.pathname === '/' ? 'text-yellow-500 dark:text-yellow-500' : ''}`} data-id="element-175">
                Avaleht
              </Link>
              {/* Mobile category accordion */}
              <div data-id="element-176">
                <button onClick={() => setCategoryMenuOpen(!categoryMenuOpen)} className={`flex items-center justify-between w-full text-left text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 font-medium transition-colors duration-200 ${location.pathname.includes('/category') ? 'text-yellow-500 dark:text-yellow-500' : ''}`} data-id="element-177">
                  <span data-id="element-178">Pood Kategooriati</span>
                  <ChevronDownIcon size={16} className={`transition-transform duration-200 ${categoryMenuOpen ? 'rotate-180' : ''}`} data-id="element-179" />
                </button>
                {categoryMenuOpen && <div className="mt-2 ml-4 space-y-2 border-l-2 border-gray-200 dark:border-gray-700 pl-4 animate-fadeIn" data-id="element-180">
                    {/* Precious Metals */}
                    <div className="mb-3" data-id="element-181">
                      <h4 className="text-sm font-bold text-gray-600 dark:text-gray-400 mb-2" data-id="element-182">
                        Väärismetallid
                      </h4>
                      {categoryGroups.metals.map(category => <Link key={category.id} to={`/category/${category.id}`} className="block py-1 text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 transition-colors duration-200" data-id="element-183">
                          {category.name}
                        </Link>)}
                    </div>
                    {/* Electronics */}
                    <div className="mb-3" data-id="element-184">
                      <h4 className="text-sm font-bold text-gray-600 dark:text-gray-400 mb-2" data-id="element-185">
                        Elektroonika
                      </h4>
                      {categoryGroups.electronics.map(category => <Link key={category.id} to={`/category/${category.id}`} className="block py-1 text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 transition-colors duration-200" data-id="element-186">
                          {category.name}
                        </Link>)}
                    </div>
                    {/* Other Categories */}
                    <div data-id="element-187">
                      <h4 className="text-sm font-bold text-gray-600 dark:text-gray-400 mb-2" data-id="element-188">
                        Rohkem Kategooriaid
                      </h4>
                      {categoryGroups.other.map(category => <Link key={category.id} to={`/category/${category.id}`} className="block py-1 text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 transition-colors duration-200" data-id="element-189">
                          {category.name}
                        </Link>)}
                      <Link to="/category/all" className="block py-1 mt-2 font-medium text-yellow-500 hover:text-yellow-600 transition-colors duration-200" data-id="element-190">
                        Vaata Kõiki Kategooriaid →
                      </Link>
                    </div>
                  </div>}
              </div>
              <Link to="/about" className={`text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 font-medium transition-colors duration-200 ${location.pathname === '/about' ? 'text-yellow-500 dark:text-yellow-500' : ''}`} data-id="element-191">
                Meist
              </Link>
              <Link to="/contact" className={`text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 font-medium transition-colors duration-200 ${location.pathname === '/contact' ? 'text-yellow-500 dark:text-yellow-500' : ''}`} data-id="element-192">
                Kontakt
              </Link>
              <div className="border-t border-gray-200 dark:border-gray-700 my-2 pt-2" data-id="element-193">
                <Link to="/account" className={`flex items-center text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-500 font-medium transition-colors duration-200 ${location.pathname === '/account' ? 'text-yellow-500 dark:text-yellow-500' : ''}`} data-id="element-194">
                  <UserIcon size={18} className="mr-2" data-id="element-195" />
                  Minu Konto
                </Link>
              </div>
            </div>
          </div>}
      </div>
    </header>;
};